import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CameraView } from "./camera";
import type { CombinedSceneValues } from "./combinedScene";
import { shouldShowGravityLegend } from "./OrientationPanel";
import { PositionDial, positionStatus, type DistributionDisplay } from "./PositionDial";

export function CombinedOrientation(props: CombinedSceneValues & {
  showExplanation?: boolean;
  distribution?: DistributionDisplay;
  onCameraViewChange(view: CameraView): void;
  onResetCamera(): void;
  gravityLegendText: string;
  showGravityLegend: boolean;
  verticalPositionThresholdDegrees?: number;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const latest = useRef(props);
  latest.current = props;
  const scene = useRef<{ update(): void; dispose(): void } | null>(null);
  const [error, setError] = useState(false);
  // OrbitControls disconnects from canvas.getRootNode(); clean up while the
  // canvas is still attached so its document key listener is removed.
  useLayoutEffect(() => {
    let disposed = false;
    void import("./combinedScene").then(({ createCombinedScene }) => {
      if (disposed || !canvas.current) return;
      scene.current = createCombinedScene(canvas.current, () => latest.current,
        (view) => latest.current.onCameraViewChange(view), () => { if (!disposed) setError(true); });
    }).catch(() => { if (!disposed) setError(true); });
    return () => { disposed = true; scene.current?.dispose(); scene.current = null; };
  }, []);
  useEffect(() => { scene.current?.update(); });
  return <div className="combined-orientation">
    <section className="orientation-panel combined-panel" data-camera-position={props.cameraView.position.join(",")} data-camera-target={props.cameraView.target.join(",")}>
      <header><strong>Combined head and torso</strong><div className="combined-header-actions"><button type="button" className="combined-camera-reset" onClick={props.onResetCamera}>Reset camera</button><details className="orientation-info">
        <summary aria-label="About combined orientation">i</summary><span role="note">Experimental gravity-relative display. Each sensor's crown points toward a common head-of-bed direction while retaining its tilt. This is a chosen display direction, not measured compass heading or head/torso yaw. Near upright or inverted upright, that direction is undefined; a deterministic fallback may visibly turn at the boundary. The position dial omits inclination. The shared neck anchor is a visual approximation, not a neck-joint measurement or posture classifier. Torso alignment is provisional.</span>
      </details></div></header>
      <div className="orientation-scene combined-scene">
        <canvas ref={canvas} aria-label="Combined head and torso 3D model; drag to orbit camera, scroll to zoom" />
        {shouldShowGravityLegend(props.showGravityDirection, props.showGravityLegend, props.gravityLegendText) && <span className="world-down-label">{props.gravityLegendText}</span>}
        {error && <p className="orientation-unavailable" role="status">3D view unavailable; position dial and signals remain available</p>}
      </div>
      <div className={"combined-pose-status" + (props.showExplanation ? "" : " orientation-inline-hidden")}>{(["head", "torso"] as const).map((id) => {
        const selected = props.selections[id];
        return <p key={id} data-pose-model={id} data-pose-state={selected?.state || "unavailable"} data-sample-time={selected?.state === "available" ? selected.sampleTime : undefined}>
          <strong>{id === "head" ? "Head" : "Torso"}:</strong> {selected?.state === "available" ? `Pose at ${selected.sampleTime.toFixed(2)} s` : positionStatus(selected, props.verticalPositionThresholdDegrees)}
        </p>;
      })}</div>
      {props.showExplanation && <p className="orientation-note">Common display direction · dial omits inclination · torso alignment provisional</p>}
    </section>
    <PositionDial showExplanation={props.showExplanation} distribution={props.distribution} selections={props.selections} appearance={props.appearance} verticalPositionThresholdDegrees={props.verticalPositionThresholdDegrees} />
  </div>;
}
